import React from 'react';
import { createBrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import AppShell from './components/AppShell';
import ToasterRoot from './components/Toaster';
import HomeScreen from './screens/HomeScreen';
import ProjectsScreen from './screens/ProjectsScreen';
import ProjectDetailScreen from './screens/ProjectDetailScreen';
import PlanScreen from './screens/PlanScreen';
import RegisterScreen from './screens/RegisterScreen';
import ReportScreen from './screens/ReportScreen';
import ReportsScreen from './screens/ReportsScreen';
import SharesScreen from './screens/SharesScreen';
import PublicReportScreen from './screens/PublicReportScreen';
// v0.39.0 — Tenant-level Contacts register. Master list lives at /contacts;
//           projects pick a subset via ProjectContacts.
import ContactsScreen from './screens/ContactsScreen';
// v0.42.0 — Tenant settings (brand name / colour / logo).
import TenantSettingsScreen from './screens/TenantSettingsScreen';
// 2026-08-14 — install the tenant's own mark types once, so pin colours and
// labels come from Settings rather than the built-in list.
import { loadTradeCatalogue } from './utils/useTrades';
// v0.45.19 — In-app /upgrade page (Free vs Pro comparison + checkout entry).
import UpgradeScreen from './screens/UpgradeScreen';
// v0.45.25 — Self-serve profile editor for subscribers (no InSpec access).
import AccountScreen from './screens/AccountScreen';
// v0.43.0 — Subbie magic-link login. Both routes render OUTSIDE
//           AppShell (subbies don't see admin chrome).
import ContactInviteScreen from './screens/ContactInviteScreen';
import MyItemsScreen from './screens/MyItemsScreen';
// v0.51.0 — InSpec client-portal hand-off landing (signed short-lived token
//           -> droppin_contact session). Renders OUTSIDE AppShell like /invite.
import PortalHandoffScreen from './screens/PortalHandoffScreen';
// v0.x — client hand-off: read-only per-Location reports (droppin_contact cookie).
import ClientLocationReportsScreen from './screens/ClientLocationReportsScreen';

// v0.2.0 — Router shell. Routes:
//   /             Home (hero + backend health + top-line stats)
//   /projects     List of projects, search, new project modal
//   /projects/:id Detail view + edit/archive
// v0.4.0 — added the plan screen (port from InSpec FieldDefectsPlan):
//   /projects/:projectId/plan                 picker — auto-picks first drawing
//   /projects/:projectId/plan/:drawingId      open a specific drawing
// v0.7.0 — added the report screen (client-side jsPDF defect report):
//   /projects/:projectId/report               full project report
//   /projects/:projectId/report?defectIds=1,2 only those defects
// v0.8.0 — shared reports:
//   /projects/:projectId/shares               list + mint share links (inside AppShell)
//   /r/:token                                 PUBLIC recipient view (OUTSIDE AppShell)
// v0.9.0 — saved report configurations:
//   /projects/:projectId/reports              list saved Report rows (config bank)
//   /projects/:projectId/report?reportId=N    open ReportScreen bound to that row
//
//   The /r/:token route renders without any DropPin admin chrome - just
//   the slim public header + report body. <Shell /> below sniffs the
//   pathname and skips AppShell for /r/* paths. This is the only
//   chrome-bypass route in the app right now.
//
// Auth wrapper goes around AppShell in v0.9.0; for now every authed
// route runs anonymous against TenantId=1 (the demo tenant). The /r/:token
// route is intentionally anonymous - token = only auth.
export default function App() {
  // 2026-08-14 — pull the tenant's mark catalogue once, at startup. Best-effort:
  // no custom types, offline, or a failed request all leave the built-in trade
  // list in place, which is exactly how the app behaved before Settings existed.
  React.useEffect(() => { loadTradeCatalogue(); }, []);

  // v0.267.0 — a DATA router (one catch-all route around the same <Routes>),
  // because the leave-page check for unsent comments (useBlocker in
  // UnsentUpdatesBar) only works under one. Made once, on first render (not at
  // import), so it reads the address after main.jsx has set the native shell up.
  return <RouterProvider router={appRouter()} />;
}

let router = null;
function appRouter() {
  if (!router) router = createBrowserRouter([{ path: '*', element: <AppRoutes /> }]);
  return router;
}

function AppRoutes() {
  return (
    <>
      {/* v0.9.3 — global toaster. Lives outside <Routes/> so toasts persist
          across route changes (e.g. a save failure on PlanScreen still shows
          after the user navigates away). */}
      <ToasterRoot />
      <Routes>
        {/* PUBLIC routes - bare, no AppShell */}
        <Route path="/r/:token" element={<PublicReportScreen />} />
        {/* v0.43.0 — Subbie magic-link landing + their assigned-items view. */}
        <Route path="/invite/:token" element={<ContactInviteScreen />} />
        {/* v0.51.0 — client-portal hand-off: /portal-handoff?t=<signed token> */}
        <Route path="/portal-handoff" element={<PortalHandoffScreen />} />
        <Route path="/my-items" element={<MyItemsScreen />} />

        {/* Everything else lives inside the AppShell */}
        <Route path="/*" element={<Shell />} />
      </Routes>
    </>
  );
}

function Shell() {
  // useLocation is here for any future "highlight nav by pathname" logic;
  // AppShell already does its own pathname check for the in-project banner.
  useLocation();
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/projects" element={<ProjectsScreen />} />
        <Route path="/projects/:id" element={<ProjectDetailScreen />} />
        {/* v0.39.0 — tenant Contacts master list. */}
        <Route path="/contacts" element={<ContactsScreen />} />
        {/* v0.42.0 — tenant brand settings. */}
        <Route path="/settings" element={<TenantSettingsScreen />} />
        {/* v0.45.19 — Upgrade comparison page. /upgrade?reason=key for
            contextual messaging when arriving from a limit-hit modal. */}
        <Route path="/upgrade" element={<UpgradeScreen />} />
        {/* v0.45.25 — Self-serve account / profile editor. */}
        <Route path="/account" element={<AccountScreen />} />
        <Route path="/projects/:projectId/plan" element={<PlanScreen />} />
        <Route path="/projects/:projectId/plan/:drawingId" element={<PlanScreen />} />
        <Route path="/projects/:projectId/register" element={<RegisterScreen />} />
        <Route path="/projects/:projectId/report" element={<ReportScreen />} />
        <Route path="/projects/:projectId/reports" element={<ReportsScreen />} />
        <Route path="/projects/:projectId/shares" element={<SharesScreen />} />
        <Route path="/projects/:projectId/site-reports" element={<ClientLocationReportsScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
